import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GHS_PICTOGRAMS } from '../../constants/ghsPictograms';

interface GhsPictogramSelectorProps {
  value: string[];
  onChange?: (codes: string[]) => void;
  readOnly?: boolean;
}

export const GhsPictogramSelector: React.FC<GhsPictogramSelectorProps> = ({
  value,
  onChange,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const toggle = (code: string) => {
    if (!onChange) return;
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  if (readOnly) {
    const selected = GHS_PICTOGRAMS.filter((p) => value.includes(p.code));
    if (selected.length === 0) return null;
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {selected.map((p) => (
          <Tooltip key={p.code} title={t(p.labelKey)} arrow>
            <Box
              component="img"
              src={p.svgUrl}
              alt={t(p.labelKey)}
              sx={{ width: 36, height: 36, display: 'block' }}
            />
          </Tooltip>
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {t('ghsPictograms.sectionTitle')}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {GHS_PICTOGRAMS.map((p) => {
          const selected = value.includes(p.code);
          return (
            <Tooltip key={p.code} title={`${p.code} — ${t(p.labelKey)}`} arrow>
              <Box
                onClick={() => toggle(p.code)}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: selected ? 'warning.main' : 'divider',
                  backgroundColor: selected ? 'warning.50' : 'transparent',
                  opacity: selected ? 1 : 0.35,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    opacity: 1,
                    borderColor: selected ? 'warning.dark' : 'text.disabled',
                  },
                }}
              >
                <Box
                  component="img"
                  src={p.svgUrl}
                  alt={t(p.labelKey)}
                  sx={{ width: 38, height: 38, display: 'block', pointerEvents: 'none' }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};
